import React, { useState, useEffect } from 'react';
import DownloadListItem, {
  type DownloadListItemProps
} from './DownloadListItem';

const PRIORITY_OVERRIDES: Record<string, number> = {
  'TerraStation Core': -1
};

function sortItems(items: DownloadListItemProps[]): DownloadListItemProps[] {
  return [...items].sort((a,b) => {
    const pa = PRIORITY_OVERRIDES[a.displayName ?? ''] ?? 0;
    const pb = PRIORITY_OVERRIDES[b.displayName ?? ''] ?? 0;
    return pb - pa;
  })
}

export type DownloadParams = {
  keywords: string;
};

export type DownloadListProps = {
  initial?: Partial<DownloadParams>;
  items?: DownloadListItemProps[];
  onSearch?: (params: DownloadParams) => void;
  onReset?: () => void;
  onChange?: (params: DownloadParams) => void;
};

const ITEMS_PER_PAGE = 10;

export default function DownloadList({
  initial,
  onSearch,
  onReset,
  onChange,
  items
}: DownloadListProps) {
  const [keywords, setKeywords] = useState(initial?.keywords || '');

  const [downloadCount, setDownloadCount] = useState<number>(0);
  const [allItems, setAllItems] = useState<DownloadListItemProps[]>([]);
  const [listItems, setListItems] = useState<DownloadListItemProps[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    if (items) {
      const sorted = sortItems(items);
      setAllItems(sorted);
      setListItems(sorted);
      setDownloadCount(sorted.length);
      setCurrentPage(1);
    } else {
      const defaultItems: DownloadListItemProps[] = [
        {
          filename: 'example_software_v1.0.zip',
          url: '#',
          uploadedAt: '2023-01-01'
        },
        {
          filename: 'another_software_v2.1.zip',
          url: '#',
          uploadedAt: '2023-02-15'
        }
      ];
      setAllItems(defaultItems);
      setListItems(defaultItems);
      setDownloadCount(defaultItems.length);
      setCurrentPage(1);
    }
  }, [items]);

  function emitChange() {
    const params: DownloadParams = { keywords };
    onChange?.(params);
  }

  function handleSearch(e?: React.FormEvent) {
    e?.preventDefault();
    const params: DownloadParams = { keywords };
    const keyword = keywords.trim().toLowerCase();
    const filtered = keyword
      ? allItems.filter((item) =>
          (item.displayName ?? item.filename ?? '')
            .toLowerCase()
            .includes(keyword)
        )
      : allItems;

    setListItems(filtered);
    setDownloadCount(filtered.length);
    setCurrentPage(1);
    onSearch?.(params);
  }

  function handleReset() {
    setKeywords('');
    setListItems(allItems);
    setDownloadCount(allItems.length);
    setCurrentPage(1);
    onReset?.();
    setTimeout(emitChange, 0);
  }

  return (
    <div className="w-full h-full relative flex flex-col items-center justify-center">
      <form
        className="w-full h-fit py-4 px-2 flex flex-col items-stretch justify-center space-y-3 border-b border-light-border"
        onSubmit={handleSearch}
        aria-label="Search"
      >
        <div className="flex flex-row gap-3 items-center">
          <label htmlFor="search-keywords" className="sr-only">
            Search by name
          </label>
          <input
            id="search-keywords"
            name="keywords"
            type="search"
            value={keywords}
            onChange={(e) => {
              setKeywords(e.target.value);
            }}
            onBlur={emitChange}
            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by name"
            className="flex-1 px-3 py-2 border border-light-border rounded focus:border-light-main duration-200 ease-in-out"
          />
          <div className="shrink-0">
            <button
              type="submit"
              className="px-6 py-2 bg-light-main text-white flex items-center cursor-pointer rounded duration-200 hover:bg-light-main-hover ease-in-out"
            >
              <svg
                className="inline h-4 w-4 mr-2 color-light-text-alt -mb-0.5"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21.4073 19.7527L16.9969 15.3422C18.0587 13.9286 18.6319 12.208 18.63 10.44C18.63 5.92406 14.9559 2.25 10.44 2.25C5.92406 2.25 2.25 5.92406 2.25 10.44C2.25 14.9559 5.92406 18.63 10.44 18.63C12.208 18.6319 13.9286 18.0587 15.3422 16.9969L19.7527 21.4073C19.9759 21.6069 20.2671 21.7135 20.5664 21.7051C20.8658 21.6967 21.1506 21.574 21.3623 21.3623C21.574 21.1506 21.6967 20.8658 21.7051 20.5664C21.7135 20.2671 21.6069 19.9759 21.4073 19.7527ZM4.59 10.44C4.59 9.28298 4.9331 8.15194 5.5759 7.18991C6.21871 6.22789 7.13235 5.47808 8.2013 5.03531C9.27025 4.59253 10.4465 4.47668 11.5813 4.70241C12.7161 4.92813 13.7584 5.48529 14.5766 6.30343C15.3947 7.12156 15.9519 8.16393 16.1776 9.29872C16.4033 10.4335 16.2875 11.6098 15.8447 12.6787C15.4019 13.7476 14.6521 14.6613 13.6901 15.3041C12.7281 15.9469 11.597 16.29 10.44 16.29C8.88906 16.2881 7.40217 15.6712 6.30548 14.5745C5.2088 13.4778 4.59186 11.9909 4.59 10.44Z"
                  fill="currentColor"
                />
              </svg>
              Search
            </button>
          </div>
          <div className="shrink-0">
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 bg-light-bg border flex items-center border-light-border rounded hover:bg-gray-50 cursor-pointer duration-200 ease-in-out"
            >
              <svg
                className="inline h-4 w-4 mr-2 -mb-0.5"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M7.41643 6.92163C8.77313 6.0151 10.3682 5.53125 11.9999 5.53125C13.0483 5.53125 13.7843 5.56665 14.2987 5.62371C14.7742 5.67646 15.1703 5.75673 15.4415 5.92093C15.5877 6.00945 15.821 6.19839 15.881 6.53362C15.9412 6.86986 15.785 7.11753 15.6984 7.22537C15.6081 7.33795 15.5078 7.40978 15.4561 7.44413C15.4258 7.46428 15.3989 7.48018 15.3783 7.49171C15.3679 7.49757 15.3586 7.50257 15.3507 7.50667C15.3483 7.50795 15.346 7.50916 15.3438 7.51028C15.3425 7.51095 15.3412 7.5116 15.34 7.51222C15.3383 7.51304 15.3368 7.51382 15.3354 7.51454L15.3333 7.51558L15.3323 7.51607C15.3318 7.51631 15.3313 7.51655 14.9999 6.84375L15.3313 7.51655C14.9597 7.69959 14.5101 7.54675 14.3271 7.17518C14.3208 7.16242 14.3149 7.14958 14.3094 7.13665C14.2576 7.12931 14.1991 7.12186 14.1333 7.11457C13.6969 7.06616 13.0225 7.03125 11.9999 7.03125C10.6649 7.03125 9.35981 7.42713 8.24978 8.16883C7.13975 8.91053 6.27459 9.96474 5.7637 11.1981C5.2528 12.4315 5.11913 13.7887 5.37958 15.0981C5.64003 16.4075 6.28291 17.6102 7.22691 18.5542C8.17092 19.4982 9.37365 20.1411 10.683 20.4016C11.9924 20.662 13.3496 20.5283 14.583 20.0174C15.8164 19.5065 16.8706 18.6414 17.6123 17.5313C18.354 16.4213 18.7499 15.1163 18.7499 13.7812C18.7499 13.367 19.0857 13.0312 19.4999 13.0312C19.9141 13.0312 20.2499 13.367 20.2499 13.7812C20.2499 15.4129 19.766 17.008 18.8595 18.3647C17.953 19.7214 16.6645 20.7788 15.157 21.4033C13.6495 22.0277 11.9907 22.1911 10.3904 21.8727C8.79004 21.5544 7.32003 20.7687 6.16625 19.6149C5.01247 18.4611 4.22673 16.9911 3.9084 15.3907C3.59008 13.7904 3.75345 12.1316 4.37788 10.6241C5.0023 9.11662 6.05972 7.82815 7.41643 6.92163Z"
                  fill="currentColor"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M11.4697 2.18842C11.7626 1.89553 12.2374 1.89553 12.5303 2.18842L16.2803 5.93842C16.5732 6.23131 16.5732 6.70619 16.2803 6.99908L12.5303 10.7491C12.2374 11.042 11.7626 11.042 11.4697 10.7491C11.1768 10.4562 11.1768 9.98131 11.4697 9.68842L14.6893 6.46875L11.4697 3.24908C11.1768 2.95619 11.1768 2.48131 11.4697 2.18842Z"
                  fill="currentColor"
                />
              </svg>
              <span>Reset</span>
            </button>
          </div>
        </div>
      </form>
      <div
        id="ts-downloadlist-results-info"
        className="w-full text-sm border-b border-light-border p-2 bg-gray-50"
      >
        Results: {downloadCount}
      </div>
      <div id="ts-downloadlist-container" className="w-full mt-2 flex flex-col">
        {listItems.length === 0 ? (
          <div className="w-full p-4 text-center text-light-text">
            No results found
          </div>
        ) : (
          <>
            <div className="w-full flex flex-col items-stretch justify-start space-y-2 p-2">
              {listItems
                .slice(
                  (currentPage - 1) * ITEMS_PER_PAGE,
                  currentPage * ITEMS_PER_PAGE
                )
                .map((item, index) => (
                  <DownloadListItem key={index} {...item} />
                ))}
            </div>

            {/* Pagination Controls */}
            {Math.ceil(listItems.length / ITEMS_PER_PAGE) > 1 && (
              <div className="w-full flex items-center justify-center gap-2 p-3 border-t border-light-border">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-light-border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed duration-200 ease-in-out cursor-pointer"
                >
                  ← Prev
                </button>

                {Array.from({
                  length: Math.ceil(listItems.length / ITEMS_PER_PAGE)
                }).map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-2 py-1 rounded duration-200 cursor-pointer ease-in-out ${
                        currentPage === pageNum
                          ? 'bg-light-main text-white'
                          : 'hover:bg-gray-50 border border-light-border'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(
                        Math.ceil(listItems.length / ITEMS_PER_PAGE),
                        p + 1
                      )
                    )
                  }
                  disabled={
                    currentPage === Math.ceil(listItems.length / ITEMS_PER_PAGE)
                  }
                  className="px-3 py-1 border border-light-border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed duration-200 cursor-pointer ease-in-out"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
